import { begrenzeBildBreite } from "./bildBreite";

/**
 * Ein Bild im Notizfeld in der Größe ändern oder herausnehmen
 * (User 23.10.2026: „Bild in der Notiz vergrößern/verkleinern … Bild
 * entfernen").
 *
 * Bewusst OHNE React, wie `einfuegen.ts` daneben: im contentEditable ist das
 * DOM die Quelle der Wahrheit. Gespeichert wird, was hier ins DOM geschrieben
 * wird — die Breite als `width`-Attribut (siehe `bildBreite.ts`), das
 * Entfernen als fehlender Marker.
 *
 * ══ DAS DOKUMENT BLEIBT, WO ES IST ═════════════════════════════════════════
 * Wer ein Bild aus der Notiz nimmt, löscht damit NICHT das Dokument dahinter
 * (`category='Notizbild'`) — auch nicht in den Papierkorb:
 *  · Strg+Z holt das Bild zurück. Läge das Dokument dann schon im
 *    Papierkorb, bliebe nach dem nächsten Laden nur ein Fehlerkasten.
 *  · Derselbe Marker kann an mehr als einer Stelle stehen (kopiert in eine
 *    andere Notiz, in einen Kommentar). Wer hier entfernt, sieht die anderen
 *    Stellen nicht.
 *  · Notizbilder stehen in KEINER Liste — auch nicht im Papierkorb. Ein
 *    dorthin verschobenes Notizbild könnte niemand wiederherstellen; es gäbe
 *    nur die Möglichkeit, ein Bild kaputt zu machen, nicht, eines zu retten.
 * Ein aus jeder Notiz herausgenommenes Notizbild ist damit nur noch eine
 * stille Datei im Speicher; aufräumen ließe sich das später serverseitig
 * (Notizbilder ohne Marker in irgendeiner Notiz).
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Wie weit ein Klick auf −/+ die Breite ändert. */
export const BILD_STUFE = 1.25;

/** Die Breite, die der Inhalt des Feldes hat — die Obergrenze eines Bilds. */
export function feldInnenBreite(feld: HTMLElement): number {
    const cs = feld.ownerDocument.defaultView?.getComputedStyle(feld);
    const polster = cs ? (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0) : 0;
    const breite = feld.clientWidth - polster;
    // jsdom (und ein noch nicht ausgelegtes Feld) meldet 0 — dann gilt die
    // Obergrenze des Attributs, die Anzeige deckelt ohnehin per CSS.
    return breite > 0 ? breite : 2000;
}

/** Wie breit das Bild gerade ist: gespeichert, sonst wie gezeichnet, sonst natürlich. */
export function aktuelleBildBreite(img: HTMLImageElement): number {
    const attr = Number(img.getAttribute("width"));
    if (Number.isFinite(attr) && attr > 0) return attr;
    const gezeichnet = img.getBoundingClientRect().width;
    if (gezeichnet > 0) return gezeichnet;
    return img.naturalWidth || 200;
}

/** Die Breite setzen — begrenzt auf 60 px bis Feldbreite. Liefert die gesetzte Breite. */
export function setzeBildBreite(img: HTMLImageElement, px: number, max: number): number {
    const breite = begrenzeBildBreite(px, max);
    img.setAttribute("width", String(breite));
    img.removeAttribute("height");
    return breite;
}

/** Eine Stufe größer (`richtung = 1`) oder kleiner (`-1`). */
export function stufeBildBreite(img: HTMLImageElement, richtung: 1 | -1, max: number): number {
    const jetzt = aktuelleBildBreite(img);
    const ziel = richtung > 0 ? jetzt * BILD_STUFE : jetzt / BILD_STUFE;
    return setzeBildBreite(img, ziel, max);
}

/**
 * Das Bild herausnehmen; der Cursor steht danach an seiner Stelle. Die Lücke
 * dahinter (`&nbsp;` aus dem Einsetzen) bleibt — sie ist Text, und Text nimmt
 * diese Geste nicht mit.
 */
export function entferneBild(img: HTMLImageElement, sel: Selection | null): void {
    const eltern = img.parentNode;
    if (!eltern) return;
    const r = img.ownerDocument.createRange();
    r.setStartBefore(img);
    r.collapse(true);
    img.remove();
    if (sel) {
        sel.removeAllRanges();
        sel.addRange(r);
    }
}
